/**
 * @fileoverview Facade hook for story planning operations.
 *
 * Provides structure metadata management and cross-domain
 * clear operations for resetting story content.
 */

'use client';

import { useStructure } from './use-structure';
import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useProject } from '@/context/project-context';
import { updateStoryStructure } from '../services';
import {
  removeAllChapters,
  deleteAllPlotSnapshots,
  deleteAllWeaveSnapshots,
  deleteAllPlotThreads,
} from '@/domains/chapter-workspace/services';
import { removeAllCharactersV2 } from '@/domains/characters/services';
import { removeAllWorldElementsV3 } from '@/domains/world-building/services/remove-all-world-elements-v3';
import { deleteAllWorldElementSnapshots } from '@/domains/world-building/services/world-element-snapshot-repository';
import { updateProjectDocument } from '@/domains/projects/services';
import { useResources } from '@/domains/resource-management/hooks/use-resources';

/**
 * Facade hook for story planning operations.
 *
 * Combines structure metadata management with cross-domain
 * operations like clearing all story content.
 */
export function useStoryPlanning() {
  const { project, setProject } = useProject();
  const structureManager = useStructure();
  const { toast } = useToast();
  const { deleteResourcesBatch } = useResources();

  const clearPlotAndEntitiesV2 = useCallback(async () => {
    // Capture previous state for rollback
    let previousStoryStructure: typeof project.storyStructure;
    let previousCharacters: typeof project.characters_v2;
    let previousChapters: typeof project.chapters;
    let previousScenes: typeof project.scenes;
    let previousPlotThreads: typeof project.plotThreads;
    let previousIdeatedCharacters: typeof project.ideatedCharacters;
    let previousIdeatedPlotOptions: typeof project.ideatedPlotOptions;
    let previousWorldElements: typeof project.worldElements_v3;
    let previousResources: typeof project.resources;
    let resourcesToDelete: typeof project.resources = [];

    setProject(prev => {
      previousStoryStructure = prev.storyStructure;
      previousCharacters = prev.characters_v2;
      previousChapters = prev.chapters;
      previousScenes = prev.scenes;
      previousPlotThreads = prev.plotThreads;
      previousIdeatedCharacters = prev.ideatedCharacters;
      previousIdeatedPlotOptions = prev.ideatedPlotOptions;
      previousWorldElements = prev.worldElements_v3;
      previousResources = prev.resources;

      // All characters and world elements are being deleted, so every resource
      // linked to any entity is guaranteed to be orphaned — no survivors remain
      // that could still reference them. Delete them all directly.
      resourcesToDelete = (prev.resources || []).filter(r =>
        (r.characterIds && r.characterIds.length > 0) ||
        (r.worldElementIds && r.worldElementIds.length > 0)
      );
      const resourceIdsToDelete = new Set(resourcesToDelete.map(r => r.id));

      return {
        ...prev,
        storyStructure: {
          ...prev.storyStructure,
          summary: undefined,
          writtenPlot: null,
          storyPlanning: undefined,
          plotSnapshots: [],
          latestPlotSnapshotId: undefined,
        },
        characters_v2: [],
        chapters: [],
        scenes: [],
        plotThreads: [],
        ideatedCharacters: [],
        ideatedPlotOptions: [],
        worldElements_v3: {
          objects: [],
          places: [],
          sets: [],
          events: [],
          organizations: [],
          species: [],
          systems: [],
        },
        resources: (prev.resources || []).filter(r => !resourceIdsToDelete.has(r.id)),
        activeWorldElementId: undefined,
        activeWorldElementType: undefined,
        updatedAt: new Date().toISOString(),
      };
    });

    try {
      await Promise.all([
        updateStoryStructure(project.id, {
          summary: null,
          writtenPlot: null,
          plotSnapshots: [],
        }, ['storyPlanning', 'latestPlotSnapshotId']),
        updateProjectDocument(project.id, {
          ideatedCharacters: [],
          ideatedPlotOptions: [],
          activeWorldElementId: null,
          activeWorldElementType: null,
        }),
        removeAllWorldElementsV3(project.id),
        deleteAllWorldElementSnapshots(project.id),
        removeAllCharactersV2(project.id),
        removeAllChapters(project.id),
        deleteAllPlotSnapshots(project.id),
        deleteAllWeaveSnapshots(project.id),
        deleteAllPlotThreads(project.id),
        resourcesToDelete.length > 0
          ? deleteResourcesBatch(resourcesToDelete)
          : Promise.resolve(),
      ]);

      toast({
        title: 'Story & Entities Cleared',
        description: 'All story content, characters, world elements, and planning data have been deleted.',
      });

    } catch (error) {
      setProject(prev => ({
        ...prev,
        storyStructure: previousStoryStructure,
        characters_v2: previousCharacters,
        chapters: previousChapters,
        scenes: previousScenes,
        plotThreads: previousPlotThreads,
        ideatedCharacters: previousIdeatedCharacters,
        ideatedPlotOptions: previousIdeatedPlotOptions,
        worldElements_v3: previousWorldElements,
        resources: previousResources,
      }));
      toast({
        variant: 'destructive',
        title: 'Deletion Failed',
        description: 'Could not clear project structure.'
      });
      console.error("Failed to clear plot and entities:", error);
    }
  }, [project.id, setProject, deleteResourcesBatch, toast]);

  return {
    // Structure
    storyStructure: structureManager.storyStructure,
    updateStructure: structureManager.updateStructure,

    // Combined Operations
    clearPlotAndEntitiesV2,
  };
}
